import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import * as emailjs from "@emailjs/browser";
import { fadeIn } from "../variants.jsx";

const Contact = ({ onClose }) => {
	const form = useRef(null);
	const [status, setStatus] = useState("idle"); // idle | sending | success | error

	const sendEmail = (e) => {
		e.preventDefault();
		setStatus("sending");

		emailjs
			.sendForm("arsenzuna01", "email_template1", form.current, {
				publicKey: "HRZxFHbB071q3qEwo",
			})
			.then(
				() => {
					setStatus("success");
					form.current.reset();
				},
				() => {
					setStatus("error");
				}
			);
	};

	const inputClasses =
		"w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder-gray-500 outline-none transition focus:border-[#8b5aa8]/60 focus:ring-2 focus:ring-[#6d3b8f]/30 disabled:opacity-50";

	const modalContact = (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div
				className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
				onClick={onClose}
			/>
			<motion.div
				variants={fadeIn("up")}
				initial="hidden"
				whileInView="show"
				viewport={{ once: false, amount: 0.4 }}
				className="relative mx-auto flex min-h-screen items-center justify-center p-4"
			>
				<div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#141019] p-8 shadow-2xl">
					<button
						onClick={onClose}
						aria-label="Close contact form"
						className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-white/10 hover:text-white"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							className="h-4 w-4"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>

					<h2 className="mb-1 text-2xl font-bold text-white">Get in touch</h2>
					<p className="mb-6 text-sm text-gray-400">
						Have a project in mind? Send a message and I'll reply as soon as I can.
					</p>

					{status === "success" ? (
						<div className="flex flex-col items-center gap-3 py-6 text-center" aria-live="polite">
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									className="h-6 w-6 text-emerald-400"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
								</svg>
							</div>
							<p className="font-medium text-white">Message sent</p>
							<p className="text-sm text-gray-400">Thanks for reaching out — talk soon.</p>
							<button
								onClick={onClose}
								className="mt-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
							>
								Close
							</button>
						</div>
					) : (
						<form ref={form} onSubmit={sendEmail} className="space-y-4" noValidate={false}>
							<div>
								<label htmlFor="from_name" className="mb-1 block text-sm font-medium text-gray-300">
									Name
								</label>
								<input
									id="from_name"
									name="from_name"
									type="text"
									required
									disabled={status === "sending"}
									placeholder="Your name"
									className={inputClasses}
								/>
							</div>

							<div>
								<label htmlFor="from_email" className="mb-1 block text-sm font-medium text-gray-300">
									Email
								</label>
								<input
									id="from_email"
									name="from_email"
									type="email"
									required
									disabled={status === "sending"}
									placeholder="you@example.com"
									className={inputClasses}
								/>
							</div>

							<div>
								<label htmlFor="message" className="mb-1 block text-sm font-medium text-gray-300">
									Message
								</label>
								<textarea
									id="message"
									name="message"
									rows="4"
									required
									disabled={status === "sending"}
									placeholder="Tell me a bit about your project..."
									className={`${inputClasses} resize-none`}
								/>
							</div>

							{status === "error" && (
								<p className="text-sm text-red-400" aria-live="polite">
									Message not sent — try again, or email me directly.
								</p>
							)}

							<button
								type="submit"
								disabled={status === "sending"}
								className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#6d3b8f] px-4 py-2.5 font-semibold text-white transition hover:bg-[#5a3175] disabled:cursor-not-allowed disabled:opacity-60"
							>
								{status === "sending" ? (
									<>
										<svg
											className="h-4 w-4 animate-spin"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
										</svg>
										Sending...
									</>
								) : (
									"Send message"
								)}
							</button>
						</form>
					)}
				</div>
			</motion.div>
		</div>
	);

	return ReactDOM.createPortal(modalContact, document.body);
};

export default Contact;